import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { Link, useParams } from "react-router-dom";
import { Button, OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import {
    BadgeAndButtonForArchive,
    Pagination,
    RenderEmptyList,
    Spinner,
    WithRoleAccess,
} from "@services/ui-components";
import {
    useGetCategoriesForMoveCategoryModalQuery,
    useGetMenuCategoriesQuery,
} from "@me-team/host/main/menu/graphql/menu.hooks";
import { ApolloError, useReactiveVar } from "@apollo/client";
import ErrorService from "../../../services/ErrorService";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import DeleteCategoryModal from "./DeleteCategoryModal";
import MoveCategoryModal from "./MoveCategoryModal";
import { ErrorModalComponent } from "@me-pos/error-modal";
import CategoryFormModal from "./CategoryFormModal";
import { useToast } from "../../../hooks/useToast";
import ToastComponent from "../../ui-components/ToastComponent/ToastComponent";
import { CategoryFormModalState, ExpandedCategoriesState } from "./interfaces";
import { CompanyAccess, MenuCategoryProxy } from "@me-team/host/main/graphql/types";
import useScrollToTopOnChange from "../../../hooks/useScrollToTopOnChange";
import { menuLanguageVersionVar } from "@me-team/host/main/menuLanguageVersions/state";
import { useMenuLanguagesData } from "../utils/hooks/useMenuLanguagesData";
import { buildPrimaryLanguageVm, buildSelectedLanguageVm } from "../SupplementsForDishesPage/utils/languageVm";
import { buildTranslationTextVm } from "../utils/translationVersions/buildTranslationTextVm";
import TranslationMissingDot from "../components/LanguageVersionSelect/TranslationMissingDot";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

interface CategoriesDishesProps {
    searchValue: string | null;
    modalCategoryState: CategoryFormModalState;
    setModalCategoryState: Dispatch<SetStateAction<CategoryFormModalState>>;
    openCategoryModal: () => void;
}

const CategoriesDishesTable: React.FC<CategoriesDishesProps> = (props) => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const basePath = process.env.REACT_APP_POS_URL;
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const selectedLangVersionCode = useReactiveVar(menuLanguageVersionVar);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(20);
    const [expandedCategories, setExpandedCategories] = useState<ExpandedCategoriesState>({});
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const [categoryToDelete, setCategoryToDelete] = useState<any>(null);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [showMoveCategoryModal, setShowMoveCategoryModal] = useState<boolean>(false);
    const [categoryToMove, setCategoryToMove] = useState<MenuCategoryProxy>(null);
    const { showToast, toastText, toggleShowToast, setShowToast } = useToast();
    const canCreateDishCategory = useRoleAccess(CompanyAccess.MenuDishCategoryCreate);
    const canEditDishCategory = useRoleAccess(CompanyAccess.MenuDishCategoryEdit);
    const canDeleteRestoreDishCategory = useRoleAccess(CompanyAccess.MenuDishCategoryDeleteRestore);
    const canOpenDishes = useRoleAccess(CompanyAccess.MenuDishListView);

    const { searchValue, modalCategoryState, setModalCategoryState, openCategoryModal } = props;

    const {
        data: categoriesDishesData,
        loading: categoriesDishesLoading,
        refetch: refetchCategoriesDishes,
    } = useGetMenuCategoriesQuery({
        variables: {
            companyId: Number(currentCompanyId),
            id: null,
            search: searchValue || null,
            page: currentPage,
            itemsPerPage,
            isDeleted: null,
            locale: selectedLangVersionCode,
        },
        skip: !currentCompanyId || !selectedLangVersionCode,
        fetchPolicy: "network-only",
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const {
        data: allCategoriesForMoveData,
        loading: allCategoriesForMoveLoading,
        refetch: refetchAllCategoriesForMove,
    } = useGetCategoriesForMoveCategoryModalQuery({
        variables: {
            companyId: Number(currentCompanyId),
            locale: selectedLangVersionCode,
        },
        skip: !currentCompanyId || !selectedLangVersionCode,
        context: {
            errorType: "local",
        },
    });

    const {
        loading: menuLanguagesLoading,
        primaryLanguage,
        languages,
    } = useMenuLanguagesData(Number(currentCompanyId));
    const primaryLanguageVm = useMemo(
        () => buildPrimaryLanguageVm(primaryLanguage, languages),
        [primaryLanguage, languages]
    );
    const selectedLanguageVm = useMemo(
        () => buildSelectedLanguageVm(selectedLangVersionCode, primaryLanguageVm, languages),
        [selectedLangVersionCode, primaryLanguageVm, languages]
    );
    const isTranslationMode = Boolean(
        selectedLanguageVm?.code && primaryLanguageVm?.code && selectedLanguageVm.code !== primaryLanguageVm.code
    );

    const toggleExpandCategory = (categoryId: number) => {
        setExpandedCategories((prev) => ({
            ...prev,
            [categoryId]: !prev[categoryId],
        }));
    };

    const categories = categoriesDishesData?.user?.company?.meMenuCategoriesPaginated?.categories || [];
    const allCategoriesForMove = allCategoriesForMoveData?.user?.company?.meMenuCategories || [];
    const pageCount = parseInt(categoriesDishesData?.user?.company?.meMenuCategoriesPaginated?.pageCount || "0");
    const totalItems = categoriesDishesData?.user?.company?.meMenuCategoriesPaginated?.totalCount || 0;
    const firstItemIndex = (currentPage - 1) * itemsPerPage + 1;
    const lastItemIndex = Math.min(currentPage * itemsPerPage, totalItems);
    const isFilterNotEmpty = Boolean(searchValue && searchValue.trim() !== "");

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [searchValue, selectedLangVersionCode]);

    useScrollToTopOnChange(currentPage);

    const handleArchiveCategory = (category: MenuCategoryProxy) => {
        if (!canDeleteRestoreDishCategory) return;
        setCategoryToDelete(category);
        setShowDeleteModal(true);
    };

    const handleEditCategory = (category: MenuCategoryProxy) => {
        if (!canEditDishCategory) return;
        setModalCategoryState({
            isOpen: true,
            mode: "edit",
            category: category,
            parentCategory: category?.parentCategory || null,
        });
    };

    const handleMoveCategory = (category: MenuCategoryProxy) => {
        if (!canEditDishCategory) return;
        setCategoryToMove(category);
        setShowMoveCategoryModal(true);
    };

    const handleRestoreCategory = (category: MenuCategoryProxy) => {
        if (!canDeleteRestoreDishCategory) return;
        setModalCategoryState({
            isOpen: true,
            mode: "restore",
            category: category,
            parentCategory: category?.parentCategory || null,
        });
    };

    const handleCreateCategory = (category: MenuCategoryProxy) => {
        if (!canCreateDishCategory) return;
        setModalCategoryState({
            isOpen: true,
            mode: "create",
            category: null,
            parentCategory: category,
        });
    };

    if (categoriesDishesLoading || menuLanguagesLoading) return <Spinner />;

    return (
        <>
            {!!categories?.length ? (
                <Table responsive>
                    <thead>
                        <tr className="fs-7">
                            <th className="col-3 px-4 text-nowrap">{t("Category / Subcategory")}</th>
                            <th className="col-4 table-thead-width">{t("description")}</th>
                            <th className="col-1">{t("Icon")}</th>
                            <th className="col-1 text-nowrap">{t("Amount of dishes")}</th>
                            <th className="col-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {categories?.map((category) => {
                            const colorForArchive = category?.deleted ? "text-secondary" : "text-dark";
                            const hasChildren = category?.childCategories.length > 0;
                            const isExpanded = expandedCategories[category?.id];
                            const nameVm = buildTranslationTextVm({
                                translatedValue: category?.name,
                                originValue: category?.nameOrigin,
                                isTranslationMode,
                            });
                            const commentVm = buildTranslationTextVm({
                                translatedValue: category?.comment,
                                originValue: category?.descriptionOrigin,
                                isTranslationMode,
                            });
                            const categoryComment = commentVm.displayText || "—";
                            return (
                                <React.Fragment key={category.id}>
                                    <tr>
                                        <td className={`px-4 align-middle ${colorForArchive}`}>
                                            <Button
                                                variant="link"
                                                className={`p-0 ${colorForArchive}`}
                                                onClick={() => toggleExpandCategory(category.id)}
                                            >
                                                {hasChildren && (
                                                    <i
                                                        className={`bi bi-chevron-right me-2 chevron-rotate ${
                                                            isExpanded ? "expanded" : ""
                                                        }`}
                                                    />
                                                )}
                                            </Button>{" "}
                                            <span className={colorForArchive}>{nameVm.displayText}</span>
                                            {nameVm.isMissingTranslation ? (
                                                <TranslationMissingDot tooltipText={t("Edit the language version")} />
                                            ) : null}
                                        </td>
                                        <td>
                                            <div
                                                className={`d-flex align-items-center justify-content-between gap-2 
                                                ${commentVm.isMissingTranslation ? "pe-3" : ""}
                                            `}
                                            >
                                                <span className={colorForArchive}>{categoryComment}</span>
                                                {commentVm.isMissingTranslation ? (
                                                    <TranslationMissingDot
                                                        tooltipText={t("Edit the language version")}
                                                    />
                                                ) : null}
                                            </div>
                                        </td>
                                        <td className={`align-middle ${colorForArchive}`}>
                                            <div className="position-relative">
                                                {category?.favicon ? (
                                                    <img
                                                        className={`icon-width ${category?.deleted ? "opacity-25" : "opacity-100"}`}
                                                        src={`${basePath}/${category.favicon.path}`}
                                                        alt={nameVm.displayText}
                                                    />
                                                ) : (
                                                    "—"
                                                )}
                                            </div>
                                        </td>
                                        <td className={`align-middle ${colorForArchive}`}>
                                            {category?.numberOfItems !== 0 && canOpenDishes ? (
                                                <Link
                                                    to={{
                                                        pathname: `/menu/dishes/branch/${branchId}`,
                                                        search: `?categoryId=${category?.id}`,
                                                    }}
                                                    className={`text-decoration-none ${category.deleted ? "text-secondary" : "text-primary"}`}
                                                >
                                                    {category.numberOfItems}
                                                </Link>
                                            ) : (
                                                category.numberOfItems
                                            )}
                                        </td>
                                        <td>
                                            <div className="d-flex gap-2 justify-content-end align-items-center">
                                                {!category?.deleted ? (
                                                    <React.Fragment>
                                                        {category.childCategories.length === 0 && (
                                                            <WithRoleAccess
                                                                permission={CompanyAccess.MenuDishCategoryEdit}
                                                            >
                                                                <OverlayTrigger
                                                                    trigger={["hover"]}
                                                                    overlay={<Tooltip>{t("Move category")}</Tooltip>}
                                                                >
                                                                    <Button
                                                                        variant="outline-secondary"
                                                                        className="py-2"
                                                                        onClick={() => handleMoveCategory(category)}
                                                                    >
                                                                        <i className="bi bi-box-arrow-right text-dark"></i>
                                                                    </Button>
                                                                </OverlayTrigger>
                                                            </WithRoleAccess>
                                                        )}
                                                        <WithRoleAccess
                                                            permission={CompanyAccess.MenuDishCategoryCreate}
                                                        >
                                                            <OverlayTrigger
                                                                trigger={["hover"]}
                                                                overlay={<Tooltip>{t("Create subcategory")}</Tooltip>}
                                                            >
                                                                <Button
                                                                    variant="outline-secondary"
                                                                    className="py-2"
                                                                    onClick={() => handleCreateCategory(category)}
                                                                >
                                                                    <i className="bi bi-plus-lg text-dark"></i>
                                                                </Button>
                                                            </OverlayTrigger>
                                                        </WithRoleAccess>
                                                        <WithRoleAccess permission={CompanyAccess.MenuDishCategoryEdit}>
                                                            <OverlayTrigger
                                                                trigger={["hover"]}
                                                                overlay={<Tooltip>{t("Edit")}</Tooltip>}
                                                            >
                                                                <Button
                                                                    variant="outline-secondary"
                                                                    className="py-2"
                                                                    onClick={() => handleEditCategory(category)}
                                                                >
                                                                    <i className="bi bi-pencil text-dark"></i>
                                                                </Button>
                                                            </OverlayTrigger>
                                                        </WithRoleAccess>
                                                        <WithRoleAccess
                                                            permission={CompanyAccess.MenuDishCategoryDeleteRestore}
                                                        >
                                                            <OverlayTrigger
                                                                trigger={["hover"]}
                                                                overlay={<Tooltip>{t("Delete")}</Tooltip>}
                                                            >
                                                                <Button
                                                                    variant="outline-secondary"
                                                                    className="py-2"
                                                                    onClick={() => handleArchiveCategory(category)}
                                                                >
                                                                    <i className="bi bi-trash text-dark"></i>
                                                                </Button>
                                                            </OverlayTrigger>
                                                        </WithRoleAccess>
                                                    </React.Fragment>
                                                ) : (
                                                    <BadgeAndButtonForArchive
                                                        hideButton={!canDeleteRestoreDishCategory}
                                                        onButtonClick={() => handleRestoreCategory(category)}
                                                    />
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                    {expandedCategories[category.id] &&
                                        category?.childCategories.length > 0 &&
                                        category?.childCategories.map((sub) => {
                                            const colorForArchiveSub = sub.deleted ? "text-secondary" : "text-dark";
                                            const subNameVm = buildTranslationTextVm({
                                                translatedValue: sub?.name,
                                                originValue: sub?.nameOrigin,
                                                isTranslationMode,
                                            });
                                            const subCommentVm = buildTranslationTextVm({
                                                translatedValue: sub?.comment,
                                                originValue: sub?.descriptionOrigin,
                                                isTranslationMode,
                                            });
                                            const subComment = subCommentVm.displayText || "—";
                                            return (
                                                <tr key={sub.id}>
                                                    <td className={`${colorForArchiveSub} ps-5`}>
                                                        <span className={colorForArchiveSub}>
                                                            {subNameVm.displayText}
                                                        </span>
                                                        {subNameVm.isMissingTranslation ? (
                                                            <TranslationMissingDot
                                                                tooltipText={t("Edit the language version")}
                                                            />
                                                        ) : null}
                                                    </td>
                                                    <td className={colorForArchiveSub}>
                                                        <div
                                                            className={`d-flex align-items-center justify-content-between gap-2 
                                                                ${commentVm.isMissingTranslation ? "pe-3" : ""}
                                                            `}
                                                        >
                                                            <span className={colorForArchiveSub}>{subComment}</span>
                                                            {subCommentVm.isMissingTranslation ? (
                                                                <TranslationMissingDot
                                                                    tooltipText={t("Edit the language version")}
                                                                />
                                                            ) : null}
                                                        </div>
                                                    </td>
                                                    <td className={`align-middle ${colorForArchive}`}>
                                                        <div className="position-relative">
                                                            {sub?.favicon ? (
                                                                <img
                                                                    className={`icon-width ${sub?.deleted ? "opacity-25" : "opacity-100"}`}
                                                                    src={`${basePath}/${sub.favicon.path}`}
                                                                    alt={subNameVm.displayText}
                                                                />
                                                            ) : (
                                                                "—"
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className={`align-middle ${colorForArchive}`}>
                                                        {sub?.numberOfItems !== 0 && canOpenDishes ? (
                                                            <Link
                                                                to={{
                                                                    pathname: `/menu/dishes/branch/${branchId}`,
                                                                    search: `?categoryId=${sub?.id}`,
                                                                }}
                                                                className={`text-decoration-none ${sub.deleted ? "text-secondary" : "text-primary"}`}
                                                            >
                                                                {sub.numberOfItems}
                                                            </Link>
                                                        ) : (
                                                            sub.numberOfItems
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div className="d-flex gap-2 gap-2 justify-content-end align-items-center">
                                                            {!sub.deleted ? (
                                                                <React.Fragment>
                                                                    <WithRoleAccess
                                                                        permission={CompanyAccess.MenuDishCategoryEdit}
                                                                    >
                                                                        <OverlayTrigger
                                                                            trigger={["hover"]}
                                                                            overlay={
                                                                                <Tooltip>{t("Move category")}</Tooltip>
                                                                            }
                                                                        >
                                                                            <Button
                                                                                variant="outline-secondary"
                                                                                className="py-2"
                                                                                onClick={() => handleMoveCategory(sub)}
                                                                            >
                                                                                <i className="bi bi-box-arrow-right text-dark"></i>
                                                                            </Button>
                                                                        </OverlayTrigger>
                                                                    </WithRoleAccess>
                                                                    <WithRoleAccess
                                                                        permission={CompanyAccess.MenuDishCategoryEdit}
                                                                    >
                                                                        <OverlayTrigger
                                                                            trigger={["hover"]}
                                                                            overlay={<Tooltip>{t("Edit")}</Tooltip>}
                                                                        >
                                                                            <Button
                                                                                variant="outline-secondary"
                                                                                className="py-2"
                                                                                onClick={() => handleEditCategory(sub)}
                                                                            >
                                                                                <i className="bi bi-pencil text-dark"></i>
                                                                            </Button>
                                                                        </OverlayTrigger>
                                                                    </WithRoleAccess>
                                                                    <WithRoleAccess
                                                                        permission={
                                                                            CompanyAccess.MenuDishCategoryDeleteRestore
                                                                        }
                                                                    >
                                                                        <OverlayTrigger
                                                                            trigger={["hover"]}
                                                                            overlay={<Tooltip>{t("Delete")}</Tooltip>}
                                                                        >
                                                                            <Button
                                                                                variant="outline-secondary"
                                                                                className="py-2"
                                                                                onClick={() =>
                                                                                    handleArchiveCategory(sub)
                                                                                }
                                                                            >
                                                                                <i className="bi bi-trash text-dark"></i>
                                                                            </Button>
                                                                        </OverlayTrigger>
                                                                    </WithRoleAccess>
                                                                </React.Fragment>
                                                            ) : (
                                                                <BadgeAndButtonForArchive
                                                                    onButtonClick={() => handleRestoreCategory(sub)}
                                                                    hideButton={
                                                                        category.deleted ||
                                                                        !canDeleteRestoreDishCategory
                                                                    }
                                                                />
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </Table>
            ) : null}

            {!!totalItems ? (
                <div className="d-flex justify-content-between align-items-center">
                    <Pagination
                        t={t}
                        pages={Number(pageCount)}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                        firstItemIndex={firstItemIndex}
                        lastItemIndex={lastItemIndex}
                        totalItems={totalItems}
                    />
                </div>
            ) : (
                <RenderEmptyList
                    isFilterNotEmpty={isFilterNotEmpty}
                    emptyFilterTitle="No data found for the given values"
                    emptyListTitle="You don't have any categories yet. Create your first category"
                    buttonName={
                        canCreateDishCategory ? (
                            <span className="fw-bold">
                                <i className="bi bi-plus-lg fw-bold me-2 w-100"></i>
                                {t("Create Category")}
                            </span>
                        ) : null
                    }
                    onClickButton={() => {
                        if (canCreateDishCategory) openCategoryModal();
                    }}
                />
            )}

            {showMoveCategoryModal && categoryToMove && (
                <MoveCategoryModal
                    show={showMoveCategoryModal}
                    handleClose={() => setShowMoveCategoryModal(false)}
                    categoryToMove={categoryToMove}
                    onMove={() => {
                        refetchCategoriesDishes();
                        refetchAllCategoriesForMove();
                        setShowMoveCategoryModal(false);
                    }}
                    categories={allCategoriesForMove}
                    categoriesLoading={allCategoriesForMoveLoading}
                    toggleShowToast={toggleShowToast}
                    canMoveCategory={canEditDishCategory}
                />
            )}

            {showDeleteModal && categoryToDelete && (
                <DeleteCategoryModal
                    show={showDeleteModal}
                    handleClose={() => setShowDeleteModal(false)}
                    category={categoryToDelete}
                    onDelete={() => {
                        refetchCategoriesDishes();
                        refetchAllCategoriesForMove();
                    }}
                    toggleShowToast={toggleShowToast}
                    canDeleteRestoreCategory={canDeleteRestoreDishCategory}
                />
            )}

            {modalCategoryState.isOpen && primaryLanguageVm && selectedLanguageVm && (
                <CategoryFormModal
                    show={modalCategoryState.isOpen}
                    handleClose={() => setModalCategoryState({ ...modalCategoryState, isOpen: false })}
                    category={modalCategoryState.category}
                    parentCategory={modalCategoryState.parentCategory}
                    mode={modalCategoryState.mode}
                    toggleShowToast={toggleShowToast}
                    onSave={() => {
                        refetchCategoriesDishes();
                        refetchAllCategoriesForMove();
                        setModalCategoryState({ ...modalCategoryState, isOpen: false });
                    }}
                    primaryLanguageVm={primaryLanguageVm}
                    selectedLanguageVm={selectedLanguageVm}
                    canCreateCategory={canCreateDishCategory}
                    canEditCategory={canEditDishCategory}
                    canDeleteRestoreCategory={canDeleteRestoreDishCategory}
                />
            )}
            {showToast && <ToastComponent show={showToast} setShow={setShowToast} text={toastText} />}
            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </>
    );
};

export default CategoriesDishesTable;
